/**
 * Errores del API en formato RFC 9457 (`application/problem+json`). El API agrega `code` (estable,
 * para decidir en la UI), `request_id` (soporte) y `fields` (errores por campo en 422).
 */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';

export interface ProblemFieldError {
  field: string;
  message: string;
  code?: string;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code?: string;
  /** El API los envía como `null` cuando no aplican. */
  detail?: string | null;
  instance?: string | null;
  request_id?: string | null;
  /** Mapa campo → mensajes (contrato del API) o lista de errores (se normaliza con `fieldErrors`). */
  fields?: Record<string, string | string[]> | ProblemFieldError[] | null;
  [extension: string]: unknown;
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.title === 'string' && typeof candidate.status === 'number';
}

export function isProblemResponse(response: Response): boolean {
  return (response.headers.get('content-type') ?? '').toLowerCase().includes(PROBLEM_CONTENT_TYPE);
}

/** Lee el problem+json de una respuesta sin consumirla (usa un clon). */
export async function readProblem(response: Response): Promise<ProblemDetails | null> {
  if (!isProblemResponse(response)) return null;
  try {
    const body: unknown = await response.clone().json();
    return isProblemDetails(body) ? body : null;
  } catch {
    return null;
  }
}

/** Mensaje por campo, listo para pintar bajo cada input. */
export function fieldErrors(problem: ProblemDetails): Record<string, string> {
  const out: Record<string, string> = {};
  const { fields } = problem;
  if (Array.isArray(fields)) {
    for (const item of fields) if (item && typeof item.field === 'string') out[item.field] ??= item.message;
  } else if (fields && typeof fields === 'object') {
    for (const [field, messages] of Object.entries(fields)) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (typeof first === 'string') out[field] = first;
    }
  }
  return out;
}

/** Error de API tipado; lo lanzan las consultas y mutaciones. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }

  get code(): string | undefined {
    return this.problem.code;
  }

  get requestId(): string | undefined {
    return this.problem.request_id ?? undefined;
  }
}

/** Construye un `ApiError` desde la respuesta y el cuerpo ya leído (si lo hay). */
export function toApiError(response: Response, body?: unknown): ApiError {
  if (isProblemDetails(body)) return new ApiError(body);
  return new ApiError({
    type: 'about:blank',
    title: response.statusText || 'Error',
    status: response.status,
  });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
